import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Slider } from '@mui/material';
import { solarData } from './SolarData';
import SolarProductionOffsetSlider from './OffsetSlider';

/**
 * The customization controls, below the packages.
 *
 * These used to be three MUI accordions with gradient headers, each holding a
 * full-bleed product card per option. That reads as a catalogue, and at this
 * point in the flow the customer has already been shown the hardware — up in
 * the package hero. What they need here is to change one thing quickly and see
 * the price move, so the options are a segmented control and the panel stays
 * quiet.
 */

const money = (value) => `$${Math.round(Number(value) || 0).toLocaleString('en-US')}`;

/* ------------------------------------------------------------- accordion */

const Chevron = styled.span`
  color: #1ba8f1;
  font-size: 0.7rem;
  line-height: 1;
  transition: transform 180ms ease;
`;

/**
 * Each section is its own card.
 *
 * They were hairline-separated rows sharing the page background, so an open
 * Solar section ran straight into the Battery header with nothing but a 1px
 * line between a segmented control and the next heading — the two read as one
 * long list. A border and a gap make the boundary obvious without adding any
 * more type.
 */
const Row = styled.details`
  border: 1px solid #e0e6ea;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;

  & + & {
    margin-top: 10px;
  }

  &[open] {
    border-color: #cfdde6;
  }

  &[open] ${Chevron} {
    transform: rotate(90deg);
  }
`;

const Summary = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 16px 18px;
  display: flex;
  align-items: baseline;
  gap: 14px;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background: #f6fafd;
  }

  &:focus-visible {
    outline: 2px solid #1ba8f1;
    outline-offset: -2px;
  }

  details[open] > & {
    border-bottom: 1px solid #eaf0f4;
    background: #f6fafd;
  }

  @media screen and (max-width: 640px) {
    padding: 14px 14px;
    /* So a long action can drop to its own line instead of being pushed off the
       side by the label and value ahead of it. */
    flex-wrap: wrap;
  }
`;

const RowLabel = styled.span`
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
  color: #0f1b24;
  min-width: 108px;
`;

const RowValue = styled.span`
  font-size: 0.85rem;
  color: #8098a8;
`;

const RowAmount = styled.span`
  margin-left: auto;
  font-size: 0.92rem;
  font-weight: 800;
  color: #0f1b24;
  font-variant-numeric: tabular-nums;
`;

const RowAction = styled.span`
  margin-left: auto;
  display: inline-flex;
  min-width: 0;

  @media screen and (max-width: 640px) {
    width: 100%;
    margin-left: 0;
    margin-top: 11px;
  }
`;

const Body = styled.div`
  padding: 20px 18px 24px;
  display: grid;
  gap: 24px;

  @media screen and (max-width: 640px) {
    padding: 18px 14px 20px;
  }
`;

/* ----------------------------------------------------------------- field */

const Field = styled.div``;

const FieldLabel = styled.span`
  display: block;
  font-size: 0.6rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 800;
  color: #8098a8;
  margin-bottom: 10px;
`;

/**
 * A segmented control on a wide screen, a list of choices on a phone.
 *
 * Wrapping the segments was the problem: hardware names are long ("REC Alpha
 * Pure-R 430W"), so on a narrow card they broke into a ragged block of boxes
 * that read as a set of buttons to press rather than one setting with several
 * options. Stacked full-width rows with a radio in front of each say "pick one"
 * without needing a word of explanation.
 */
const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  border: 1px solid #e0e6ea;
  border-radius: 8px;
  overflow: hidden;
  width: fit-content;
  max-width: 100%;

  @media screen and (max-width: 640px) {
    flex-direction: column;
    flex-wrap: nowrap;
    width: 100%;
  }
`;

const Option = styled.button`
  appearance: none;
  border: 0;
  border-right: 1px solid #e0e6ea;
  background: ${({ $active }) => ($active ? '#1BA8F1' : '#fff')};
  color: ${({ $active }) => ($active ? '#04222F' : '#3C4E5A')};
  font-weight: ${({ $active }) => ($active ? 800 : 400)};
  font-family: inherit;
  font-size: 0.82rem;
  padding: 10px 15px;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;

  &:last-child {
    border-right: 0;
  }

  &:hover {
    background: ${({ $active }) => ($active ? '#1BA8F1' : '#f6fafd')};
  }

  &:focus-visible {
    outline: 2px solid #1ba8f1;
    outline-offset: -3px;
  }

  @media screen and (max-width: 640px) {
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
    text-align: left;
    padding: 13px 14px;
    border-right: 0;
    border-bottom: 1px solid #e0e6ea;

    /* A full-width row filled solid blue is a lot of colour for a setting, and
       it stops looking like a row in a list. The tint plus the filled radio
       carries the selection on its own. */
    background: ${({ $active }) => ($active ? '#eaf6fe' : '#fff')};
    color: ${({ $active }) => ($active ? '#0b7fbd' : '#3C4E5A')};

    &:last-child {
      border-bottom: 0;
    }

    &:hover {
      background: ${({ $active }) => ($active ? '#eaf6fe' : '#f6fafd')};
    }

    /* The inset white ring is what makes this read as a radio rather than a
       dot: a filled circle with a gap inside its border. */
    &::before {
      content: '';
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      border: 2px solid ${({ $active }) => ($active ? '#1BA8F1' : '#cbd7df')};
      background: ${({ $active }) => ($active ? '#1BA8F1' : '#fff')};
      box-shadow: ${({ $active }) => ($active ? 'inset 0 0 0 3px #fff' : 'none')};
    }
  }
`;

const Specs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 20px;
  margin-top: 12px;
  font-size: 0.72rem;
  color: #8098a8;

  b {
    color: #3c4e5a;
    font-weight: 700;
  }

  a {
    color: #0b7fbd;
    font-weight: 700;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

/* ---------------------------------------------------------------- slider */

const SlideRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 22px;
  align-items: center;
  max-width: 560px;
`;

const SlideValue = styled.span`
  font-size: 0.85rem;
  font-weight: 800;
  color: #0f1b24;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const BrandSlider = styled(Slider)`
  color: #1ba8f1;

  .MuiSlider-thumb {
    width: 18px;
    height: 18px;
  }

  .MuiSlider-rail {
    opacity: 0.28;
  }

  .MuiSlider-mark {
    background-color: #cfe0ea;
  }

  .MuiSlider-markLabel {
    font-size: 0.68rem;
    color: #8098a8;
  }
`;

const Note = styled.p`
  margin: 10px 0 0;
  font-size: 0.72rem;
  color: #8098a8;
`;

/**
 * Sits inside the accordion summary, so it has to stop the click from reaching
 * `<summary>` — otherwise adding a charger also toggles the section open or
 * shut under the customer's cursor.
 */
const HeaderButton = styled.button`
  appearance: none;
  border: 1px solid #1ba8f1;
  border-radius: 8px;
  background: #eaf6fe;
  color: #0b7fbd;
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 800;
  padding: 8px 13px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #d8eefc;
  }

  &:focus-visible {
    outline: 2px solid #1ba8f1;
    outline-offset: 2px;
  }

  @media screen and (max-width: 640px) {
    /* The label carries a product name and a price ("Add a Tesla Wall Connector
       for $2,500"), which no phone fits on one line — held at nowrap it simply
       ran off the card. On its own row it can use the full width and wrap. */
    width: 100%;
    white-space: normal;
    padding: 11px 13px;
  }
`;

const RemoveButton = styled.button`
  appearance: none;
  border: 1px solid #e0e6ea;
  border-radius: 8px;
  background: #fff;
  color: #6b8090;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 9px 14px;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    border-color: #cbd7df;
    color: #3c4e5a;
  }
`;

const AddButton = styled.button`
  appearance: none;
  border: 1px solid #1ba8f1;
  border-radius: 8px;
  background: #eaf6fe;
  color: #0b7fbd;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 800;
  padding: 10px 16px;
  cursor: pointer;

  &:hover {
    background: #d8eefc;
  }
`;

/* ----------------------------------------------------------------- parts */

/**
 * One collapsible section.
 *
 * `open` is held in React rather than left to the browser. A bare
 * `<details open={...}>` is a controlled attribute, so every re-render — and a
 * slider fires plenty — would snap the section back to whatever the expression
 * said, closing the panel under the customer's hand as an EV charger count
 * reached zero. `autoOpen` only ever opens: a package that brings a battery
 * reveals the battery section, but removing one leaves it where it is.
 */
const Section = ({ label, value, amount, action, autoOpen, children }) => {
  const [open, setOpen] = useState(Boolean(autoOpen));
  const wasAutoOpen = useRef(Boolean(autoOpen));

  useEffect(() => {
    if (autoOpen && !wasAutoOpen.current) setOpen(true);
    wasAutoOpen.current = Boolean(autoOpen);
  }, [autoOpen]);

  return (
    <Row open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <Summary>
        <Chevron>&#9656;</Chevron>
        <RowLabel>{label}</RowLabel>
        <RowValue>{value}</RowValue>
        {action ? <RowAction>{action}</RowAction> : <RowAmount>{amount}</RowAmount>}
      </Summary>
      <Body>{children}</Body>
    </Row>
  );
};

const OptionGroup = ({ label, options, selected, onSelect }) => (
  <Field>
    <FieldLabel>{label}</FieldLabel>
    <Options role="group" aria-label={label}>
      {options.map((option) => (
        <Option
          key={option.name}
          type="button"
          $active={option.name === selected?.name}
          aria-pressed={option.name === selected?.name}
          onClick={() => onSelect(option)}
        >
          {option.name}
        </Option>
      ))}
    </Options>
    <SpecLine option={selected} />
  </Field>
);

/** The specs that used to sit on each product card, condensed to one line. */
const SpecLine = ({ option, extra }) => {
  if (!option) return null;
  const entries = [
    ...Object.entries(option.column1 || {}),
    ...Object.entries(option.column2 || {}),
  ];

  return (
    <Specs>
      {entries.map(([key, value]) => (
        <span key={key}>
          {key} <b>{value}</b>
        </span>
      ))}
      {extra}
      {option['Spec Sheet'] && (
        <a href={option['Spec Sheet']} target="_blank" rel="noreferrer">
          Spec sheet
        </a>
      )}
    </Specs>
  );
};

const batteryMarks = [0, 1, 2, 3, 4, 5, 6, 7].map((value) => ({ value, label: String(value) }));

const Sections = styled.div``;

const SectionsLabel = styled.h2`
  font-size: 0.68rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 800;
  color: #8098a8;
  margin: 26px 0 12px;
`;

/* ---------------------------------------------------------------- export */

const CustomizeSection = ({
  hardware,
  responseState,
  updateHardware,
  onModuleChange,
  onInverterChange,
  onBatteryChange,
  onBatteryQuantityChange,
  onServiceQuantityChange,
  onAddSolar,
}) => {
  const panels = hardware?.module?.watts
    ? Math.round((Number(hardware.systemSize) || 0) / hardware.module.watts)
    : 0;
  const capacity = Number(hardware?.batteryCapacity) || 0;
  const hasSolar = Number(hardware?.systemSize) > 0;

  return (
    <Sections>
      <SectionsLabel>Customize your system</SectionsLabel>
      <Section
        label="Solar"
        autoOpen
        value={
          hasSolar
            ? `${hardware.module.name} · ${hardware.inverter.name} · ${panels} panels`
            : 'Not included'
        }
        amount={hasSolar ? money(hardware.solarCost) : '—'}
      >
          <OptionGroup
            label="Modules"
            options={solarData.modules.options}
            selected={hardware.module}
            onSelect={onModuleChange}
          />
          <OptionGroup
            label="Inverter"
            options={solarData.inverters.options}
            selected={hardware.inverter}
            onSelect={onInverterChange}
          />
          {hasSolar && (
            <Note>
              {money(hardware.solarCost)} for {panels} panels ·{' '}
              ${Number(hardware.solarPPW || 0).toFixed(2)} per watt
            </Note>
          )}
          {hasSolar ? (
            // Carries its own System Size / Offset readout and heading, so it
            // does not get a second field label on top.
            <SolarProductionOffsetSlider
              estimateSize={responseState.estimateSize}
              hardware={hardware}
              updateHardware={updateHardware}
              solarProductionMonthly={responseState.solarProductionMonthly}
            />
          ) : (
            <AddButton type="button" onClick={onAddSolar}>
              Add solar
            </AddButton>
          )}
      </Section>

      <Section
        label="Battery"
        autoOpen={hardware.batteryQuantity > 0}
        value={
          hardware.batteryQuantity > 0
            ? `${hardware.batteryQuantity} × ${hardware.battery.name} · ${capacity} kWh`
            : 'Not included'
        }
        amount={hardware.batteryQuantity > 0 ? money(hardware.batteryCost) : '—'}
      >
          <OptionGroup
            label="Battery"
            options={solarData.batteries.options}
            selected={hardware.battery}
            onSelect={onBatteryChange}
          />
          <Field>
            <FieldLabel>How many</FieldLabel>
            <SlideRow>
              <BrandSlider
                value={hardware.batteryQuantity}
                onChange={(event, value) => onBatteryQuantityChange(value)}
                aria-label="Number of batteries"
                valueLabelDisplay="auto"
                step={1}
                marks={batteryMarks}
                min={0}
                max={7}
              />
              <SlideValue>
                {hardware.batteryQuantity > 0 ? `${capacity} kWh` : 'No battery'}
              </SlideValue>
            </SlideRow>
            {hardware.recommendedBattQuantity > 0 && (
              <Note>
                {hardware.recommendedBattQuantity} recommended for your usage
                {hardware.backupHours > 0 && hardware.batteryQuantity > 0
                  ? ` · ${hardware.backupHours} hours of backup at 75% usage`
                  : ''}
              </Note>
            )}
          </Field>
      </Section>

      <Section
        label="EV charger"
        autoOpen={hardware.serviceQuantity > 0}
        value={hardware.serviceQuantity > 0 ? hardware.services.name : ''}
        amount={hardware.serviceQuantity > 0 ? money(hardware.serviceCost) : null}
        action={
          hardware.serviceQuantity > 0 ? null : (
            <HeaderButton
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onServiceQuantityChange(1);
              }}
            >
              Add a {hardware.services.name} for {money(hardware.services.price)}
            </HeaderButton>
          )
        }
      >
          <Field>
            <SpecLine option={hardware.services} />
          </Field>
          {hardware.serviceQuantity > 0 && (
            <RemoveButton type="button" onClick={() => onServiceQuantityChange(0)}>
              Remove charger
            </RemoveButton>
          )}
      </Section>
    </Sections>
  );
};

export default CustomizeSection;
